/**
 * Central keyboard handler attached to the host element. Manages navigation, paging,
 * and edit lifecycle triggers while respecting active form field interactions.
 */
import { GridClasses } from '../constants';
import type { GridHost } from '../types';
import { FOCUSABLE_EDITOR_SELECTOR } from './rows';
import { clearCellFocus, isRTL } from './utils';
import { fromVirtualScrollTop, toVirtualScrollTop } from './virtualization';

/** Commit active row edit if the editing plugin provides this method. */
function tryCommitEdit(grid: GridHost): void {
  if (typeof grid.commitActiveRowEdit === 'function') grid.commitActiveRowEdit();
}

function isFormField(el: HTMLElement | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return true;
  if (el.isContentEditable) return true;
  return false;
}

// #region Keyboard Handler
export function handleGridKeyDown(grid: GridHost, e: KeyboardEvent): void {
  // Dispatch to plugin system first - if any plugin handles it, stop here
  if (grid._dispatchKeyDown?.(e)) {
    return;
  }

  const maxRow = grid._rows.length - 1;
  const maxCol = grid._visibleColumns.length - 1;
  const editing = grid._activeEditRows !== undefined && grid._activeEditRows !== -1;
  const col = grid._visibleColumns[grid._focusCol];
  const colType = col?.type;
  const path = e.composedPath?.() ?? [];
  const target = (path.length ? path[0] : e.target) as HTMLElement | null;
  // Keyboard events whose target lives inside the grid host but OUTSIDE the
  // rows body (toolpanel inputs/buttons, shell-header controls, light-DOM
  // children of <tbw-grid>) own their own keyboard interactions. Treating
  // ArrowUp/ArrowDown/Enter on such a target as cell navigation steals the
  // user's actual interaction target on the very next render cycle.
  // Cells and the grid host itself remain valid navigation targets.
  if (target && target !== grid && !target.closest?.('.rows-body')) return;
  if (isFormField(target) && (e.key === 'Home' || e.key === 'End')) return;
  if (isFormField(target) && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
    if ((target as HTMLInputElement).tagName === 'INPUT' && (target as HTMLInputElement).type === 'number') return;
  }
  // Let arrow left/right navigate within text inputs instead of moving cells
  if (isFormField(target) && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) return;
  // Let Enter/Escape be handled by the input's own handlers first
  if (isFormField(target) && (e.key === 'Enter' || e.key === 'Escape')) return;
  if (editing && colType === 'select' && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) return;
  switch (e.key) {
    case 'Tab': {
      e.preventDefault();
      const forward = !e.shiftKey;
      if (forward) {
        if (grid._focusCol < maxCol) grid._focusCol += 1;
        else {
          tryCommitEdit(grid);
          if (grid._focusRow < maxRow) {
            grid._focusRow += 1;
            grid._focusCol = 0;
          }
        }
      } else {
        if (grid._focusCol > 0) grid._focusCol -= 1;
        else if (grid._focusRow > 0) {
          if (grid._activeEditRows === grid._focusRow) tryCommitEdit(grid);
          grid._focusRow -= 1;
          grid._focusCol = maxCol;
        }
      }
      ensureCellVisible(grid);
      return;
    }
    case 'ArrowDown':
      if (editing) tryCommitEdit(grid);
      grid._focusRow = Math.min(maxRow, grid._focusRow + 1);
      e.preventDefault();
      break;
    case 'ArrowUp':
      if (editing) tryCommitEdit(grid);
      grid._focusRow = Math.max(0, grid._focusRow - 1);
      e.preventDefault();
      break;
    case 'ArrowRight': {
      // In RTL mode, ArrowRight moves toward the start (lower column index)
      const rtl = isRTL(grid);
      if (rtl) {
        grid._focusCol = Math.max(0, grid._focusCol - 1);
      } else {
        grid._focusCol = Math.min(maxCol, grid._focusCol + 1);
      }
      e.preventDefault();
      break;
    }
    case 'ArrowLeft': {
      // In RTL mode, ArrowLeft moves toward the end (higher column index)
      const rtl = isRTL(grid);
      if (rtl) {
        grid._focusCol = Math.min(maxCol, grid._focusCol + 1);
      } else {
        grid._focusCol = Math.max(0, grid._focusCol - 1);
      }
      e.preventDefault();
      break;
    }
    case 'Home':
      if (e.ctrlKey || e.metaKey) {
        // CTRL+Home: navigate to first row, first cell
        if (editing) tryCommitEdit(grid);
        grid._focusRow = 0;
        grid._focusCol = 0;
      } else {
        // Home: navigate to first cell in current row
        grid._focusCol = 0;
      }
      e.preventDefault();
      ensureCellVisible(grid, { forceScrollLeft: true });
      return;
    case 'End':
      if (e.ctrlKey || e.metaKey) {
        // CTRL+End: navigate to last row, last cell
        if (editing) tryCommitEdit(grid);
        grid._focusRow = maxRow;
        grid._focusCol = maxCol;
      } else {
        // End: navigate to last cell in current row
        grid._focusCol = maxCol;
      }
      e.preventDefault();
      ensureCellVisible(grid, { forceScrollRight: true });
      return;
    case 'PageDown':
      grid._focusRow = Math.min(maxRow, grid._focusRow + 20);
      e.preventDefault();
      break;
    case 'PageUp':
      grid._focusRow = Math.max(0, grid._focusRow - 20);
      e.preventDefault();
      break;
    // NOTE: Enter key is handled by EditingPlugin. If no plugin handles it,
    // we dispatch the unified cell-activate event for custom handling.
    case 'Enter': {
      const rowIndex = grid._focusRow;
      const colIndex = grid._focusCol;
      const column = grid._visibleColumns[colIndex];
      const row = grid._rows[rowIndex];
      const field = column?.field ?? '';
      const value = field && row ? (row as Record<string, unknown>)[field] : undefined;
      const cellEl = grid.querySelector(`[data-row="${rowIndex}"][data-col="${colIndex}"]`) as HTMLElement | undefined;

      const detail = {
        rowIndex,
        colIndex,
        column,
        field,
        value,
        row,
        cellEl,
        trigger: 'keyboard' as const,
        originalEvent: e,
      };

      // Emit unified cell-activate event
      const activateEvent = new CustomEvent('cell-activate', {
        cancelable: true,
        detail,
      });
      grid.dispatchEvent(activateEvent);

      if (activateEvent.defaultPrevented) {
        e.preventDefault();
        return;
      }
      break;
    }
    default:
      return;
  }
  ensureCellVisible(grid);
}
// #endregion

// #region Cell Visibility
/**
 * Options for ensureCellVisible to control scroll behavior.
 */
interface EnsureCellVisibleOptions {
  /** Force scroll to the leftmost position (for Home key) */
  forceScrollLeft?: boolean;
  /** Force scroll to the rightmost position (for End key) */
  forceScrollRight?: boolean;
  /** Force horizontal scroll even in edit mode (for Tab navigation) */
  forceHorizontalScroll?: boolean;
}

/**
 * Scroll the viewport (virtualized or static) so the focused cell's row is visible
 * and apply visual focus styling / tabindex management.
 */
export function ensureCellVisible(grid: GridHost, options?: EnsureCellVisibleOptions): void {
  if (grid._virtualization?.enabled) {
    const { rowHeight, container, viewportEl, scrollMapping } = grid._virtualization;
    // container is the faux scrollbar element that handles actual scrolling
    // viewportEl is the visible area element that has the correct height
    const scrollEl = container as HTMLElement | undefined;
    const visibleHeight = viewportEl?.clientHeight ?? scrollEl?.clientHeight ?? 0;
    if (scrollEl && visibleHeight > 0) {
      // `y` is in raw row-content space (rowHeight × index). Above the
      // browser height cap (#326), `scrollEl.scrollTop` is in clamped
      // spacer space — the two coordinate systems are not interchangeable.
      // Translate scrollTop into virtual space for the comparison, and
      // translate the target back into native space when writing it.
      // For sub-cap datasets the mapping is identity so this is a no-op.
      const y = grid._focusRow * rowHeight;
      const virtualScrollTop = scrollMapping
        ? toVirtualScrollTop(scrollEl.scrollTop, scrollMapping)
        : scrollEl.scrollTop;
      if (y < virtualScrollTop) {
        scrollEl.scrollTop = scrollMapping ? fromVirtualScrollTop(y, scrollMapping) : y;
      } else if (y + rowHeight > virtualScrollTop + visibleHeight) {
        const target = y - visibleHeight + rowHeight;
        scrollEl.scrollTop = scrollMapping ? fromVirtualScrollTop(target, scrollMapping) : target;
      }
    }
  }
  // Skip refreshVirtualWindow when in edit mode to avoid wiping editors
  const isEditing = (grid._activeEditRows !== undefined && grid._activeEditRows !== -1) || !!grid._isGridEditMode;
  if (!isEditing) {
    grid.refreshVirtualWindow(false);
  }
  clearCellFocus(grid._bodyEl);
  // Clear previous aria-selected markers
  Array.from(grid._bodyEl.querySelectorAll('[aria-selected="true"]')).forEach((el) => {
    el.setAttribute('aria-selected', 'false');
  });
  const rowIndex = grid._focusRow;
  const vStart = grid._virtualization.start ?? 0;
  const vEnd = grid._virtualization.end ?? grid._rows.length;
  if (rowIndex >= vStart && rowIndex < vEnd) {
    const rowEl = grid._bodyEl.querySelectorAll('.data-grid-row')[rowIndex - vStart] as HTMLElement | null;
    // Try exact column match first, then query by data-col, then fallback to first cell (for full-width group rows)
    let cell = rowEl?.children[grid._focusCol] as HTMLElement | undefined;
    if (!cell || !cell.classList?.contains('cell')) {
      cell = (rowEl?.querySelector(`.cell[data-col="${grid._focusCol}"]`) ??
        rowEl?.querySelector('.cell[data-col]')) as HTMLElement | undefined;
    }
    if (cell) {
      cell.classList.add('cell-focus');
      cell.setAttribute('aria-selected', 'true');

      // Horizontal scroll: ensure focused cell is visible in the horizontal scroll area
      // The .tbw-scroll-area element handles horizontal scrolling
      // Skip horizontal scrolling when in edit mode to prevent scroll jumps when editors are created
      // Unless forceHorizontalScroll is set (e.g., for Tab navigation while editing)
      const scrollArea = grid.querySelector('.tbw-scroll-area') as HTMLElement | null;
      if (scrollArea && cell && (!isEditing || options?.forceHorizontalScroll)) {
        // Handle forced scroll for Home/End keys - always scroll to edge
        if (options?.forceScrollLeft) {
          scrollArea.scrollLeft = 0;
        } else if (options?.forceScrollRight) {
          scrollArea.scrollLeft = scrollArea.scrollWidth - scrollArea.clientWidth;
        } else {
          // Get scroll boundary offsets from plugins (e.g., pinned columns)
          // This allows plugins to report how much of the scroll area they obscure
          // and whether the focused cell should skip scrolling (e.g., pinned cells are always visible)
          const offsets = grid._getHorizontalScrollOffsets?.(rowEl ?? undefined, cell) ?? { left: 0, right: 0 };

          if (!offsets.skipScroll) {
            // Get cell position relative to the scroll area
            const cellRect = cell.getBoundingClientRect();
            const scrollAreaRect = scrollArea.getBoundingClientRect();
            // Calculate the cell's position relative to scroll area's visible region
            const cellLeft = cellRect.left - scrollAreaRect.left + scrollArea.scrollLeft;
            const cellRight = cellLeft + cellRect.width;
            // Adjust visible boundaries to account for plugin-reported offsets
            const visibleLeft = scrollArea.scrollLeft + offsets.left;
            const visibleRight = scrollArea.scrollLeft + scrollArea.clientWidth - offsets.right;
            // Scroll horizontally if needed
            if (cellLeft < visibleLeft) {
              scrollArea.scrollLeft = cellLeft - offsets.left;
            } else if (cellRight > visibleRight) {
              scrollArea.scrollLeft = cellRight - scrollArea.clientWidth + offsets.right;
            }
          }
        }
      }

      if (isEditing && cell.classList.contains(GridClasses.EDITING)) {
        // Editing cell: focus the editor input inside it
        const focusTarget = cell.querySelector(FOCUSABLE_EDITOR_SELECTOR) as HTMLElement | null;
        if (focusTarget && document.activeElement !== focusTarget) {
          try {
            focusTarget.focus({ preventScroll: true });
          } catch {
            /* empty */
          }
        }
      } else if (isEditing && !cell.contains(document.activeElement)) {
        // Active edit row but this cell isn't the editing cell — focus it
        // so Tab navigation within the row can attach editors
        if (!cell.hasAttribute('tabindex')) cell.setAttribute('tabindex', '-1');
        try {
          cell.focus({ preventScroll: true });
        } catch {
          /* empty */
        }
      } else if (!isEditing) {
        // NOT editing: keep focus on the grid element (tabindex=0) rather than
        // individual cells. In a virtualized grid, cells can be detached by
        // subsequent render cycles (e.g., SelectionPlugin's requestAfterRender
        // → RAF → row recycling). A detached focused cell causes activeElement
        // to revert to <body>, breaking keyboard navigation.
        // Visual focus is managed by the .cell-focus CSS class + data-has-focus.
        //
        // BUT: don't steal focus from a real focused descendant (toolpanel
        // input, shell-header button, registered overlay control). Cell focus
        // is virtual; if the user is actively typing in a non-cell input, we
        // must leave that input alone. We only reclaim focus when nothing
        // meaningful inside the grid currently holds it (active element is
        // outside grid, on the grid host itself, or on a bare cell).
        const active = document.activeElement;
        const meaningful =
          active instanceof HTMLElement &&
          active !== grid &&
          typeof grid.contains === 'function' &&
          grid.contains(active) &&
          !active.classList.contains('cell') &&
          !active.closest('.cell');
        if (!meaningful && active !== grid) {
          grid.focus({ preventScroll: true });
        }
      }
    }
  }
}
// #endregion
